// Checking in at a planned place (F5): the phone's position is read once, compared with the
// place here, and dropped. Only "near: yes/no" is recorded (shared/places.ts). A near check-in
// turns the place's Crate ghost into a toy and, by the rules, earns a play. Works offline (GPS).
import type { Place } from '../../shared/pack/schema'
import type { RewardRules } from '../../shared/rewards'
import { distanceM } from '../domain/places'
import { record } from './events'

export type CheckinResult = { near: true; earned: number } | { near: false; metres: number } | { error: 'denied' | 'unavailable' }

function position(): Promise<[number, number]> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('unavailable'))
    navigator.geolocation.getCurrentPosition((p) => resolve([p.coords.latitude, p.coords.longitude]), reject, { enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 })
  })
}

export async function checkIn(opts: { place: Place; itemId?: string; personId: string; rules: RewardRules; day: string; alreadyFound: boolean }): Promise<CheckinResult> {
  let here: [number, number]
  try {
    here = await position()
  } catch (e) {
    return { error: (e as GeolocationPositionError | undefined)?.code === 1 ? 'denied' : 'unavailable' }
  }
  const metres = distanceM(here, opts.place.at)
  const near = metres <= opts.rules.checkin.radiusM
  await record('checkin', { placeId: opts.place.id, ...(opts.itemId ? { itemId: opts.itemId } : {}), near }, opts.personId)
  if (!near) return { near, metres }
  const earned = opts.rules.checkin.on && !opts.alreadyFound ? opts.rules.checkin.plays : 0
  if (earned) await record('reward.earned', { trigger: 'checkin', ref: opts.place.id, plays: earned, day: opts.day }, opts.personId)
  return { near, earned }
}
